import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import {
    CommonServiceProxy,
    GlobalSearchInputDto,
    GlobalSearchOutputDto,
    OneDriveLinks,
    OneDriveServiceProxy
} from '@shared/service-proxies/service-proxies';

/** A single dropdown row produced by the search service. */
export interface MentionSearchRow {
    /** Display name (file name, task title, client name). */
    name: string;
    /** Breadcrumb / subtitle (e.g. "Trovve > User Stories"). */
    breadcrumb: string;
    /** Folders are grouped into 'file' per product spec; a client COMPANY is a 'client' like a person. */
    mentionType: 'file' | 'task' | 'client' | 'project';
    /** Raw backend DTO so the host can build a chip without re-querying. */
    rawData: any;
    /** Pre-extracted fields the chip needs (file/folder/task/client). */
    entityId: number;
    entityType: number;
    extension: string;
    isFolder: boolean;
    fileId: string;
    driveId: string;
    siteId: string;
    folderId: string;
    fileSize: number;
    webUrl: string;
    taskId: number;
    contactId: number;
    companyId: number;
    /** Optional tooltip-specific subtitle (used for clients to surface
     *  "type › stage" while the dropdown shows project counts). */
    tooltipBreadcrumb: string;
}

/** Result of a single search call, already split by group. */
export interface MentionSearchResult {
    query: string;
    files: MentionSearchRow[];
    tasks: MentionSearchRow[];
    clients: MentionSearchRow[];
    projects: MentionSearchRow[];
}

const MAX_PER_GROUP: number = 5;
const SEARCH_DEBOUNCE_MS: number = 150;
const RECENT_FILES_LIMIT: number = 5;

@Injectable({ providedIn: 'root' })
export class MentionSearchService {

    private _queryStream: Subject<string> = new Subject<string>();
    private _searchResults: Subject<MentionSearchResult> = new Subject<MentionSearchResult>();

    constructor(
        private _commonService: CommonServiceProxy,
        private _oneDriveService: OneDriveServiceProxy
    ) {
        this._queryStream
            .pipe(
                debounceTime(SEARCH_DEBOUNCE_MS),
                switchMap((query: string) => this._dispatchQuery(query))
            )
            .subscribe((result: MentionSearchResult) => {
                this._searchResults.next(result);
            });
    }

    /** Observable of debounced search results. Hosts subscribe once and call
     *  `search()` to push new queries through the pipe. */
    public results$(): Observable<MentionSearchResult> {
        return this._searchResults.asObservable();
    }

    /** Push a new query through the debounced search pipeline. */
    public search(query: string): void {
        this._queryStream.next(query || '');
    }

    /** Recent files, kept after the first successful fetch — see `fetchRecentFiles`. */
    private _recentFilesCache: MentionSearchRow[] | null = null;

    /** Fetch the user's most-recent files (used for the empty-query default state).
     *  Returns immediately — no debounce. Capped at RECENT_FILES_LIMIT (5)
     *  rows, but the backend may return fewer if the user only has a few
     *  recently-accessed files.
     *
     *  CACHED after the first success, because this answers a bare "/" — a keystroke. Re-hitting the backend
     *  on every one left the popup on its empty state for the whole round-trip, every time, in both the notes
     *  and the comment composer. The list is user-scoped and slow-moving; anything newer is still reachable by
     *  typing, which searches live. */
    public fetchRecentFiles(): Observable<MentionSearchRow[]> {
        if (this._recentFilesCache) {
            return of(this._recentFilesCache);
        }
        return new Observable<MentionSearchRow[]>((subscriber) => {
            this._oneDriveService.getMyRecentFiles('', false, 1).subscribe(
                (response: any) => {
                    let rows: MentionSearchRow[] = [];
                    if (!response.isError && Array.isArray(response.responseData)) {
                        const truncated: OneDriveLinks[] = response.responseData.slice(0, RECENT_FILES_LIMIT);
                        rows = truncated.map((link: OneDriveLinks) => this._oneDriveLinkToRow(link));
                        // Only a real answer is worth keeping — an error/empty response would pin the popup
                        // to "no recent files" for the rest of the session.
                        if (rows.length > 0) {
                            this._recentFilesCache = rows;
                        }
                    }
                    subscriber.next(rows);
                    subscriber.complete();
                },
                () => {
                    subscriber.next([]);
                    subscriber.complete();
                }
            );
        });
    }

    // --- Internals ---

    private _dispatchQuery(query: string): Observable<MentionSearchResult> {
        const trimmed: string = (query || '').trim();
        if (trimmed.length === 0) {
            const empty: MentionSearchResult = { query: trimmed, files: [], tasks: [], clients: [], projects: [] };
            return of(empty);
        }
        return new Observable<MentionSearchResult>((subscriber) => {
            const input: GlobalSearchInputDto = new GlobalSearchInputDto();
            input.searchText = trimmed;
            input.isTasks = true;
            input.isFiles = true;
            input.isContacts = true;
            // Projects stay OFF — out of scope for `/` (decided 2026-08-05). The EntityType 2 mapping
            // below is kept so they'd land in the right group if this is ever switched on.
            input.isProjects = false;
            input.skipCount = 0;
            input.takeCount = 25;
            this._commonService.globalSearch(input).subscribe(
                (response: any) => {
                    const result: MentionSearchResult = {
                        query: trimmed,
                        files: [],
                        tasks: [],
                        clients: [],
                        projects: []
                    };
                    if (!response.isError && Array.isArray(response.responseData)) {
                        this._splitByEntityType(response.responseData, result);
                    }
                    subscriber.next(result);
                    subscriber.complete();
                },
                () => {
                    const empty: MentionSearchResult = { query: trimmed, files: [], tasks: [], clients: [], projects: [] };
                    subscriber.next(empty);
                    subscriber.complete();
                }
            );
        });
    }

    private _splitByEntityType(rows: GlobalSearchOutputDto[], target: MentionSearchResult): void {
        rows.forEach((dto: GlobalSearchOutputDto) => {
            const row: MentionSearchRow = this._globalSearchDtoToRow(dto);
            if (row.mentionType === 'file' && target.files.length < MAX_PER_GROUP) {
                target.files.push(row);
                return;
            }
            if (row.mentionType === 'task' && target.tasks.length < MAX_PER_GROUP) {
                target.tasks.push(row);
                return;
            }
            if (row.mentionType === 'client' && target.clients.length < MAX_PER_GROUP) {
                target.clients.push(row);
                return;
            }
            if (row.mentionType === 'project' && target.projects.length < MAX_PER_GROUP) {
                target.projects.push(row);
                return;
            }
        });
    }

    private _globalSearchDtoToRow(dto: GlobalSearchOutputDto): MentionSearchRow {
        const entityType: number = dto.entityType || 0;
        let mentionType: 'file' | 'task' | 'client' | 'project' = 'file';
        let name: string = '';
        let breadcrumb: string = '';
        let entityId: number = 0;
        let extension: string = '';
        let isFolder: boolean = false;
        let fileId: string = '';
        let driveId: string = '';
        let siteId: string = '';
        let folderId: string = '';
        let fileSize: number = 0;
        let webUrl: string = '';
        let taskId: number = 0;
        let contactId: number = 0;
        let companyId: number = 0;
        let tooltipBreadcrumb: string = '';

        switch (entityType) {
            case 1: {
                // Task
                mentionType = 'task';
                name = dto.title || '';
                const taskBreadcrumbParts: string[] = [];
                if (dto.assignedToUserId && dto.contactName) {
                    taskBreadcrumbParts.push(dto.contactName);
                }
                if (dto.projectName) {
                    taskBreadcrumbParts.push(dto.projectName);
                }
                if (dto.sectionName) {
                    taskBreadcrumbParts.push(dto.sectionName);
                }
                breadcrumb = taskBreadcrumbParts.join(' › ');
                entityId = dto.taskId || 0;
                taskId = dto.taskId || 0;
                break;
            }
            case 2: {
                // Project. `Vf_GlobalSearch_Get_All` emits EntityType 2 for these; before this case they
                // fell through to the file branch and rendered as a nameless file row.
                mentionType = 'project';
                name = dto.projectName || dto.title || '';
                breadcrumb = dto.clientName || '';
                entityId = dto.projectId || 0;
                break;
            }
            case 5: {
                // Client COMPANY (EntityType 5 in the SP). Same fall-through bug as projects — a company
                // hit came back looking like a file. Rendered in the Clients group next to persons.
                mentionType = 'client';
                name = dto.contactCompanyName || dto.contactName || '';
                breadcrumb = [dto.contactType, dto.clientStage].filter((s) => !!s).join(' · ');
                entityId = dto.contactCompanyId || 0;
                companyId = dto.contactCompanyId || 0;
                break;
            }
            case 3: {
                // Contact (person)
                mentionType = 'client';
                name = dto.contactName || '';
                const contactBreadcrumbParts: string[] = [];
                if (dto.contactCompanyName) {
                    contactBreadcrumbParts.push(dto.contactCompanyName);
                }
                if (dto.contactType) {
                    contactBreadcrumbParts.push(dto.contactType);
                }
                breadcrumb = contactBreadcrumbParts.join(' · ');
                // Tooltip uses "type › stage" (clientStage falls back to a
                // dash so the chevron is still meaningful when the stage is
                // unset; the chevron itself is what the spec asks for).
                const contactType: string = dto.contactType || '';
                const contactStage: string = dto.clientStage || '';
                if (contactType || contactStage) {
                    tooltipBreadcrumb = [contactType, contactStage].filter(s => s.length > 0).join(' › ');
                }
                entityId = dto.contactId || 0;
                contactId = dto.contactId || 0;
                companyId = dto.contactCompanyId || 0;
                break;
            }
            case 4: {
                // File or folder. `filePath` is the file's webUrl (a long URL
                // that doesn't make sense as a breadcrumb). `fileSourceName` is
                // the human-readable drive/site name (e.g. "Trovve > User
                // Stories"), which matches the breadcrumb the drag-and-drop
                // upload path puts on its token.
                mentionType = 'file';
                name = dto.fileName || '';
                breadcrumb = dto.fileSourceName || '';
                entityId = dto.fileId || 0;
                extension = (dto.extension || '').toLowerCase();
                isFolder = extension === 'folder' || extension === 'documentlibrary';
                fileId = dto.keyId || '';
                driveId = dto.msDriveId || '';
                siteId = dto.msSiteId || '';
                folderId = dto.msFolderId || '';
                webUrl = dto.filePath || '';
                break;
            }
            case 5: {
                // Company
                mentionType = 'client';
                name = dto.contactCompanyName || dto.clientName || '';
                const clientCount: number = dto.companyContactCount || 0;
                const clientType: string = dto.contactType || '';
                const clientStage: string = dto.clientStage || '';
                const companyBreadcrumbParts: string[] = [];
                if (clientType) {
                    companyBreadcrumbParts.push(clientType);
                }
                if (clientCount > 0) {
                    companyBreadcrumbParts.push(clientCount + (clientCount === 1 ? ' contact' : ' contacts'));
                }
                breadcrumb = companyBreadcrumbParts.join(' · ');
                // Tooltip uses "type › stage" (per story spec) — distinct
                // from the dropdown's "type · N contacts" breadcrumb.
                if (clientType || clientStage) {
                    tooltipBreadcrumb = [clientType, clientStage].filter(s => s.length > 0).join(' › ');
                }
                entityId = dto.contactCompanyId || 0;
                companyId = dto.contactCompanyId || 0;
                break;
            }
            default: {
                // Unknown entity type — render as a generic file row so the
                // dropdown doesn't crash. The host shouldn't see this in
                // practice since we set isProjects=false.
                mentionType = 'file';
                name = dto.title || dto.fileName || '';
                break;
            }
        }

        return {
            name: name,
            breadcrumb: breadcrumb,
            mentionType: mentionType,
            rawData: dto,
            entityId: entityId,
            entityType: entityType,
            extension: extension,
            isFolder: isFolder,
            fileId: fileId,
            driveId: driveId,
            siteId: siteId,
            folderId: folderId,
            fileSize: fileSize,
            webUrl: webUrl,
            taskId: taskId,
            contactId: contactId,
            companyId: companyId,
            tooltipBreadcrumb: tooltipBreadcrumb
        };
    }

    private _oneDriveLinkToRow(link: OneDriveLinks): MentionSearchRow {
        const extension: string = (link.extension || '').toLowerCase();
        const isFolder: boolean = extension === 'folder' || extension === 'documentlibrary';
        const breadcrumbParts: string[] = [];
        if (link.siteName) {
            breadcrumbParts.push(link.siteName);
        } else if (link.driveName) {
            breadcrumbParts.push(link.driveName);
        }
        return {
            name: link.name || '',
            breadcrumb: breadcrumbParts.join(' › '),
            mentionType: 'file',
            rawData: link,
            entityId: 0,
            entityType: 4,
            extension: extension,
            isFolder: isFolder,
            fileId: link.id || '',
            driveId: link.driveId || '',
            siteId: link.siteId || '',
            folderId: link.folderId || '',
            fileSize: 0,
            webUrl: link.webUrl || '',
            taskId: 0,
            contactId: 0,
            companyId: 0,
            tooltipBreadcrumb: ''
        };
    }
}
